/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useEffect, useState, useCallback, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';

import { Box, IconButton } from '@mui/material';
import { GpsFixed, GpsNotFixed } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import * as S from './style';
import MapSources from '../../../../pages/Maps/MapSources';
import MapLayers from '../../../../pages/Maps/MapLayers';
import MapIconWrapper from '../../../MapIcon';
import { useFilePath } from '../../../../hooks/useFilePath';
import CustomTooltip from '../../atoms/CustomTooltip';
import { createAccuracyCircle } from '../../../../pages/Maps/utils';
import ToggleSatelliteButton from '../../molecules/ToggleSatelliteButton';

mapboxgl.accessToken = process.env.REACT_APP_MAP_TOKEN;

const satelliteStyle = process.env.REACT_APP_MAP_STYLE_SATELLITE;
const defaultStyle = process.env.REACT_APP_MAP_STYLE_DEFAULT;

const DEFAULT_ZOOM = 14;

const MiniMap = ({
  centerPosition,
  features,
  selectedMarker,
  handleItemSelect,
  simplified,
}) => {
  const { buildIconPath } = useFilePath();
  const { t } = useTranslation();
  const [mapState, setMapState] = useState();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markersList, setMarkersList] = useState([]);
  const [zoomLevel, setZoomLevel] = useState();
  const [following, setFollowing] = useState(true);
  const [currentStyle, setCurrentStyle] = useState(defaultStyle);
  const containerId = useId();

  const isSattelite = useMemo(() => {
    return currentStyle === satelliteStyle;
  }, [currentStyle]);

  const changeMap = useCallback(() => {
    const nextStyle =
      currentStyle === defaultStyle ? satelliteStyle : defaultStyle;
    mapState?.setStyle(nextStyle);
    setCurrentStyle(nextStyle);
  }, [currentStyle, mapState]);

  const initMapData = useCallback(async () => {
    return new Promise((resolve, reject) => {
      try {
        const s = MapSources().sources.map(source =>
          mapState?.addSource(
            Object.keys(source)[0],
            source[Object.keys(source)[0]],
          ),
        );
        const l = MapLayers().layers.map(layer => mapState?.addLayer(layer));
        resolve([s, l]);
      } catch (err) {
        reject(new Error(err));
      }
    }).finally(() => {
      setMapLoaded(true);
    });
  }, [mapState]);

  const createMarkers = useCallback(() => {
    const mList = [];
    features.forEach(feature => {
      const el = document.createElement('div');
      const { id, icon, name, timestamp, status } = feature.properties;
      const center = [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1],
      ];
      const radius = feature.properties.data?.hacc;

      if (
        center[0] == null ||
        center[1] == null ||
        Number.isNaN(center[0]) ||
        Number.isNaN(center[1])
      )
        return;
      if (radius) {
        // fixed id to allow only one sensor accuracy to be displayed per time
        createAccuracyCircle(mapState, 'accuracy', center, radius);
      }
      const customId = `${containerId}-${id}`;

      const iconEl = createPortal(
        <MapIconWrapper
          id={customId}
          src={buildIconPath(icon)}
          name={name}
          status={status?.toLowerCase()}
          onClick={() => handleItemSelect(feature.properties)}
          customStyle={
            timestamp === selectedMarker?.timestamp
              ? { opacity: 1 }
              : { opacity: 0.6 }
          }
        />,
        el,
      );
      const newMarker = new mapboxgl.Marker(el)
        .setLngLat(center)
        .addTo(mapState);
      document.getElementById(`marker_${timestamp ?? customId}`)?.remove();
      newMarker
        .getElement()
        .setAttribute('id', `marker_${timestamp ?? customId}`);
      mList.push({ marker: newMarker, iconEl });
    });
    setMarkersList(mList);
  }, [
    buildIconPath,
    containerId,
    features,
    handleItemSelect,
    mapState,
    selectedMarker?.timestamp,
  ]);

  const cleanMarkers = useCallback(() => {
    markersList.forEach(marker => {
      marker.marker.remove();
    });
    setMarkersList([]);
  }, [markersList]);

  const updateMarkers = useCallback(() => {
    if (
      mapState
        ?.queryRenderedFeatures()
        .filter(feature => feature.layer.id === 'clusters').length === 0
    ) {
      cleanMarkers();
      createMarkers();
    }
  }, [cleanMarkers, createMarkers, mapState]);

  const flyToLastPosition = useCallback(() => {
    if (selectedMarker?.position && following) {
      const center = [
        selectedMarker.position?.lng || selectedMarker.lng,
        selectedMarker.position?.lat || selectedMarker.lat,
      ];

      if (
        center[0] == null ||
        center[1] == null ||
        Number.isNaN(center[0]) ||
        Number.isNaN(center[1])
      )
        return;

      mapState?.flyTo({
        center,
      });
    }
  }, [selectedMarker, mapState, following]);

  useEffect(() => {
    const mapbox = new mapboxgl.Map({
      container: containerId,
      style: currentStyle,
      center: centerPosition,
      zoom: DEFAULT_ZOOM,
    });
    setMapState(mapbox);

    // Resize observer for parent container
    const resizeObserver = new ResizeObserver(() => {
      mapbox.resize();
    });
    resizeObserver.observe(document.getElementById(containerId));

    return () => {
      resizeObserver.disconnect();
      mapbox.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapState && !mapLoaded) {
      setMapLoaded(true);
      mapState.on('load', () => {
        initMapData()
          .then(() => {
            if (!simplified) {
              const nav = new window.mapboxgl.NavigationControl();
              mapState.addControl(nav, 'bottom-right');
            }
          })
          .catch(err => err);
      });
      mapState.on('zoomend', () => {
        setZoomLevel(mapState.getZoom());
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState]);

  useEffect(() => {
    updateMarkers();
    flyToLastPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyToLastPosition]);

  useEffect(() => {
    cleanMarkers();
    if (
      mapState &&
      mapState
        .queryRenderedFeatures()
        .filter(feature => feature.layer.id === 'clusters').length === 0
    ) {
      createMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomLevel, features]);

  return (
    <>
      <S.ContainerMap id={containerId}>
        {!simplified && (
          <>
            {features.length === 1 && (
              <CustomTooltip
                title={
                  following ? t('tools_menu.unfollow') : t('tools_menu.follow')
                }
                arrow
                placement="left"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '9.5rem',
                    zIndex: 3,
                    right: ' 0.6rem',
                    background: '#fff',
                    borderRadius: '0.25rem',
                  }}
                >
                  <IconButton
                    sx={{ width: '30px', height: '30px' }}
                    onClick={() => {
                      setFollowing(prev => !prev);
                      flyToLastPosition();
                    }}
                  >
                    {following ? (
                      <GpsFixed sx={{ color: '#383838' }} />
                    ) : (
                      <GpsNotFixed sx={{ color: '#383838' }} />
                    )}
                  </IconButton>
                </Box>
              </CustomTooltip>
            )}
            <ToggleSatelliteButton
              isSmall
              changeMap={changeMap}
              isLoadingMap={false}
              isSattelite={isSattelite}
            />
          </>
        )}
      </S.ContainerMap>
      {markersList.length > 0 && markersList.map(i => i.iconEl)}
    </>
  );
};

MiniMap.defaultProps = {
  handleItemSelect: () => null,
  selectedMarker: {},
};

MiniMap.propTypes = {
  centerPosition: PropTypes.array.isRequired,
  features: PropTypes.array.isRequired,
  selectedMarker: PropTypes.shape({
    asset_id: PropTypes.string,
    timestamp: PropTypes.string,
    date: PropTypes.string,
    location: PropTypes.string,
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    position: PropTypes.oneOfType([
      PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      PropTypes.string,
    ]),
  }),
  handleItemSelect: PropTypes.func,
  simplified: PropTypes.bool,
};

export default MiniMap;
